<script>
  import { createEventDispatcher, onMount } from "svelte";
  // import monoFontUrl from "../assets/fonts/BerkeleyMono-Regular.woff2";
  // import characterFontUrl from "../assets/fonts/Hershey-Noailles-Futura-Simplex-Regular.otf";
  import {
    createRandomEncoding,
    encodingToHex,
    DEFAULT_MARGIN_FACTOR,
    renderToCanvas,
  } from "bitframes";
  import { getContext } from "svelte";
  import { downloadCanvas, downloadEncoding } from "../util/save";
  import { colorSpace, getBackground } from "../util/constants";
  // import { drawCanvas2D } from "../util/worker.pool";

  export let encoding = createRandomEncoding();

  const canvasEnv = getContext("canvas");

  const dispatch = createEventDispatcher();
  let canvas, context;
  let container;

  let tmpCanvas, tmpContext;

  export let fillHeight = true;
  export let topAlign = false;
  export let zoom = false;
  export let margin = true;
  export let displayPadding = 20;
  export let hd = true;
  export let hotkeys = false;
  export let canvasResolution = 2048;
  export let autoCanvasResolution = false;
  export let initialDrawBackground = true;
  export let lazyRender = false;
  export let allowClickZoom = false;
  export let allowTouchZoom = false;
  export let worker = false;
  export let pool = null;
  // export let restrictMaxSize = false;

  const aspect = 1;

  let touchZoomOverride = false;
  let curMousePosition = null;
  let curParentRect = null;

  let viewX = "0",
    viewY = "0";
  let width;
  let height;

  let viewWidth = "100%";
  let viewHeight = "auto";

  let _lastParams = null;
  let _hasBeenSeen = false;

  let raf = null;
  let _curZoom = zoom;
  $: {
    _curZoom = zoom;
    if (allowTouchZoom && touchZoomOverride) _curZoom = true;

    // _curZoom = allowTouchZoom ? touchZoomOverride : zoom;
  }

  let idealCanvasWidth = computeIdealResolution();

  // $: canvasResolution = 2048;
  $: _curZoom, updateSize(), requestDrawFrame();
  $: hd, margin, requestDrawFrame();
  $: encoding, requestDrawFrame();
  $: {
    width = autoCanvasResolution
      ? idealCanvasWidth
      : Math.round(canvasResolution);
    height = Math.round(width / aspect);
    drawBackground();
    requestDrawFrame();
  }

  function computeIdealResolution() {
    return window.innerWidth < 720 ? 3000 : 4096;
  }

  onMount(() => {
    if (canvasEnv) canvasEnv.save = save;
    updateSize();

    const intObserver = new IntersectionObserver((entries) => {
      for (let entry of entries) {
        const visible = entry.isIntersecting;
        const target = entry.target;
        const wasVisible = target.dataset.visible === "true";
        target.dataset.visible = visible ? "true" : "false";
        if (visible !== wasVisible) {
          if (visible) {
            // console.log("make visible");
            _hasBeenSeen = true;
            requestDrawFrame();
          } else {
            // removeFromRenderQueue(target);
          }
          // if (visible) start();
          // else stop();
        }
      }
    });
    intObserver.observe(container);

    const observer = new ResizeObserver((entries) => {
      resize(entries[0].target.getBoundingClientRect());

      // this returns top:0 for some reason
      // resize(entries[0].contentRect);
    });
    observer.observe(container);
    context = canvas.getContext("2d", { colorSpace });
    drawBackground();
    requestDrawFrame();
    return () => {
      cancelAnimationFrame(raf);
      intObserver.unobserve(container);
      observer.unobserve(container);
    };
  });

  function drawBackground() {
    if (initialDrawBackground && context) {
      const system = encoding ? encoding[0] : 0x00;
      const background = getBackground({ system });
      context.fillStyle = background;
      context.fillRect(0, 0, canvas.width, canvas.height);
    }
  }

  async function save(opts = {}) {
    if (!canvas) return;

    const size = 4096;

    if (!tmpCanvas) {
      tmpCanvas = document.createElement("canvas");
      tmpContext = tmpCanvas.getContext("2d", { colorSpace });
    }

    tmpCanvas.width = size;
    tmpCanvas.height = size;
    tmpContext.clearRect(0, 0, size, size);
    if (pool) {
      pool.draw(encodingToHex(encoding), {
        canvas: tmpCanvas,
        context: tmpContext,
        hatch: true,
        hatchContours: false,
      });
    } else if (worker) {
      drawCanvas2D({
        width: size,
        height: size,
        encoding,
        context: tmpContext,
        hatch: Boolean(hd),
        hatchContours: Boolean(hd),
      });
    } else {
      renderToCanvas({
        width: size,
        height: size,
        encoding,
        context: tmpContext,
        hatch: Boolean(hd),
        hatchContours: Boolean(hd),
      });
    }
    await downloadEncoding(tmpCanvas, {
      ...opts,
      encoding,
    });
    // await downloadCanvas(tmpCanvas, opts);
  }

  function updateSize() {
    if (container) resize(container.getBoundingClientRect());
  }

  function resize(contentRect) {
    curParentRect = {
      width: contentRect.width,
      height: contentRect.height,
      top: contentRect.top,
      left: contentRect.left,
    };

    const parentSize = contentRect;
    const parentAspect = parentSize.width / parentSize.height;
    const widthBased = _curZoom
      ? parentAspect >= aspect
      : parentAspect < aspect;
    const factor = margin ? 1 / (1 - (0.01 + DEFAULT_MARGIN_FACTOR) * 2) : 1;
    const mult = _curZoom ? factor : 1;
    const padding = _curZoom ? 0 : displayPadding;
    // viewWidth = `${parentSize.height}px`;
    // viewHeight = `${parentSize.height}px`;
    // console.log(parentSize.height);
    // console.log(parentSize);
    // const tx = 0.5;
    // const ty = 0.0;
    let vw, vh;
    if (widthBased) {
      const dim = mult * (parentSize.width - padding * 2);
      vw = dim;
      vh = Math.round(dim / aspect);
      viewWidth = `${vw}px`;
      viewHeight = `${vh}px`;
    } else {
      const dim = mult * (parentSize.height - padding * 2);
      vh = dim;
      vw = Math.round(dim * aspect);
      viewHeight = `${vh}px`;
      viewWidth = `${vw}px`;
    }
    // const maxSizePercent = 0.8;
    // if (
    //   vw > window.innerWidth * maxSizePercent &&
    //   vh > window.innerHeight * maxSizePercent
    // ) {
    //   const newWidth = vw * 0.7;
    //   const newHeight = Math.round(newWidth / aspect);
    //   viewWidth = `${newWidth}px`;
    //   viewHeight = `${newHeight}px`;
    // }
  }

  function requestDrawFrame() {
    cancelAnimationFrame(raf);
    raf = requestAnimationFrame(drawFrame);
  }

  function encodingsMatch(a, b) {
    const h0 = typeof a === "string" ? a : encodingToHex(a);
    const h1 = typeof b === "string" ? b : encodingToHex(b);
    return h0 === h1;
  }

  function didChange(oldParams, newParams) {
    if (!oldParams) return true;
    return (
      oldParams.width !== newParams.width ||
      oldParams.height !== newParams.height ||
      !encodingsMatch(oldParams.encoding, newParams.encoding) ||
      oldParams.margin !== newParams.margin ||
      oldParams.hatch !== newParams.hatch ||
      oldParams.hatchContours !== newParams.hatchContours
    );
  }

  function drawFrame() {
    if (!context) return;
    if (lazyRender && !_hasBeenSeen) return;
    const old = _lastParams;
    const params = {
      width,
      height,
      encoding,
      context,
      margin: margin ? undefined : 0,
      hatch: Boolean(hd),
      hatchContours: Boolean(hd),
    };
    if (!old || didChange(old, params)) {
      if (pool) {
        pool.draw(encodingToHex(encoding), {
          ...params,
          canvas: context.canvas,
          context,
        });
      } else if (worker) {
        drawCanvas2D(params);
      } else {
        renderToCanvas(params);
      }
      // drawCanvas2D(params);
      _lastParams = params;
    }
  }

  function onClick(ev) {
    if (allowClickZoom) {
      ev.preventDefault();
      zoom = !zoom;
    }
  }

  function updateMouse(ev) {
    if (!curMousePosition) curMousePosition = [0, 0];
    curMousePosition[0] = ev.clientX;
    curMousePosition[1] = ev.clientY;
    if (!curParentRect) return;
    const left = curMousePosition[0] - curParentRect.left;
    const top = curMousePosition[1] - curParentRect.top;
    const u = Math.max(0, Math.min(1, left / curParentRect.width));
    const v = Math.max(0, Math.min(1, top / curParentRect.height));
    // const radius = Math.hypot(curParentRect.width, curParentRect.height) / 2;
    // viewX = (-((u * 2 - 1) / 2) * curParentRect.width) / 2;
    // viewY = (((v * 2 - 1) / 2) * curParentRect.height) / 2;
    const size = Math.min(curParentRect.width, curParentRect.height) / 4;
    // viewX = (-(u * 2 - 1) / 1) * size;
    // viewY =
    //   (-(v * 2 - 1) / 1) * size * (curParentRect.width / curParentRect.height);
  }

  function windowResized() {
    idealCanvasWidth = computeIdealResolution();
  }
</script>

<svelte:window on:resize|passive={windowResized} />
<div
  class="container noselect"
  class:topAlign
  class:fillHeight
  class:allowClickZoom
  class:fullscreen={_curZoom}
  class:zoom={_curZoom}
  role="none"
  on:touchstart|nonpassive={(ev) => {
    if (allowTouchZoom) {
      if (zoom) zoom = false;
      touchZoomOverride = true;
      ev.preventDefault();
      ev.stopImmediatePropagation();
    }
  }}
  on:touchend|nonpassive={(ev) => {
    if (allowTouchZoom) {
      touchZoomOverride = false;
      ev.preventDefault();
      ev.stopImmediatePropagation();
    }
  }}
  on:click={(ev) => {
    // if (!allowTouchZoom) {
    dispatch("click", ev);
    updateMouse(ev);
    onClick(ev);
    // }
  }}
  on:doubleclick={(ev) => {
    if (allowClickZoom) ev.preventDefault();
  }}
  bind:this={container}
>
  <canvas
    class="noselect"
    bind:this={canvas}
    class:hd
    class:zoom={_curZoom}
    {width}
    {height}
    style={`width: ${viewWidth}; height: ${viewHeight}; transform: translate(${_curZoom ? viewX : 0}px, ${_curZoom ? viewY : 0}px);`}
  ></canvas>
</div>

<svelte:body
  on:mousemove|passive={(ev) => {
    updateMouse(ev);
  }}
  on:keydown|nonpassive={(ev) => {
    if (hotkeys && canvas && ev.key == "s" && (ev.metaKey || ev.ctrlKey)) {
      dispatch("save", { originalEvent: ev, canvas });
    }
  }}
/>

<style>
  .container {
    overflow: hidden;
    display: flex;
    justify-content: center;
    align-items: center;
    width: 100%;
    /* aspect-ratio: 1; */
    padding: 0px;
    box-sizing: border-box;
    margin: 0;
    padding: 0;
    position: relative;
  }
  .allowClickZoom {
    cursor: zoom-in;
  }
  .allowClickZoom.zoom {
    cursor: zoom-out;
  }
  .topAlign {
    align-items: flex-start;
  }
  .fillHeight {
    height: 100%;
  }
  .container.fullscreen {
    padding: 0px;
    margin: 0;
    padding: 0;
  }
  canvas {
    transform-origin: center center;
    position: absolute;
    image-rendering: pixelated;
    /* box-shadow:
      5px 10px 20px rgba(0, 0, 0, 0.1),
      2px 2px 4px rgba(0, 0, 0, 0.1); */
    /* box-shadow:
      2px 0px 0 black,
      0px 2px 0 black,
      2px 2px 0 black; */
  }
  canvas.hd {
    image-rendering: crisp-edges;
  }
</style>
